// OsteotomyHistory.js - Updated with bulk delete
import React from "react";
import useTranslation from "../../../../../hooks/i18n/useTranslation";
import { ButtonSize } from "../../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../../classes/enums/IconsEnum";
import { ActNatures } from "../../../../../classes/enums/SurgicalActsEnum";
import { roundToUnit } from "../../../../../classes/utils/NumbersUtils";
import { generateSurgicalActKey } from "../../../../../classes/utils/SurgeryUtils";
import Button from "../../../../../components/button/Button";
import IconButton from "../../../../../components/iconButton/IconButton";
import WidgetBox from "../../../../../components/widgetBox/WidgetBox";
import "../History.css";

const OsteotomyItem = ({
  osteotomy,
  onDelete,
  onEdit,
  isSelected,
  onToggleSelect,
  disabled,
  showCheckbox = true,
  showActions = true,
}) => {
  const { t } = useTranslation();
  return (
    <div className={`history-item ${isSelected ? "selected" : ""}`}>
      <div className="history-row">
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(osteotomy)}
            disabled={disabled}
            className="history-item-checkbox"
          />
        )}
        <div className="App-text-medium">{osteotomy.vertebra}</div>
        <div className="App-text-medium">
          {roundToUnit(osteotomy.value).toString() + "°"}
        </div>
        <div className="App-text-medium">{osteotomy.osteotomyType}</div>
        {showActions && (
          <div className="interaction-buttons">
            <IconButton
              icon={IconsEnum.EDIT}
              size={ButtonSize.SMALL}
              isCircle={true}
              noBgColor
              onClick={() => onEdit()}
              disabled={disabled}
              title={t("208")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const OsteotomyHistory = ({
  onDeleteSurgicalAct,
  onBulkDeleteSurgicalActs,
  onEditSurgicalAct,
  currentSurgicalActs,
  comparedSurgicalActs = [],
  compareMode = false,
  currentPlanLabel = "Plan 1",
  comparedPlanLabel = "Plan 2",
}) => {
  const [isLoading, setLoading] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState(new Set());

  const getActsArray = actsSource => {
    if (!actsSource) {
      return [];
    }
    return Array.isArray(actsSource) ? actsSource : Object.values(actsSource);
  };

  const surgicalActsArray = getActsArray(currentSurgicalActs);
  const comparedActsArray = getActsArray(comparedSurgicalActs);

  // Filter only the acts that are osteotomies
  const osteotomyActs = surgicalActsArray.filter(
    act => act.actNature === ActNatures.Osteotomy
  );
  const comparedOsteotomies = comparedActsArray.filter(
    act => act.actNature === ActNatures.Osteotomy
  );

  const handleDeleteOsteotomy = async osteotomyToDelete => {
    try {
      if (isLoading) {
        return;
      }

      setLoading(true);
      if (onDeleteSurgicalAct) {
        await onDeleteSurgicalAct(osteotomyToDelete);
      } else {
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (isLoading || selectedItems.size === 0) {
        return;
      }

      setLoading(true);

      const selectedOsteotomies = osteotomyActs.filter(act =>
        selectedItems.has(generateSurgicalActKey(act))
      );

      if (onBulkDeleteSurgicalActs) {
        await onBulkDeleteSurgicalActs(selectedOsteotomies);
        // Clear selection after successful delete
        setSelectedItems(new Set());
      } else {
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleToggleSelect = osteotomy => {
    const key = generateSurgicalActKey(osteotomy);
    const newSelected = new Set(selectedItems);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === osteotomyActs.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all
      const allKeys = new Set(osteotomyActs.map(generateSurgicalActKey));
      setSelectedItems(allKeys);
    }
  };

  const editOsteotomy = osteotomy => {
    if (onEditSurgicalAct) {
      onEditSurgicalAct(osteotomy);
    } else {
    }
  };

  if (osteotomyActs.length === 0 && !compareMode) {
    return null;
  }

  const hasSelection = selectedItems.size > 0;
  const isAllSelected = selectedItems.size === osteotomyActs.length;

  const renderPlanSection = (label, items) => (
    <div className="history-compare-plan">
      <div className="history-compare-plan-title App-text-medium">
        {label}
      </div>
      {items.length === 0 ? (
        <div className="history-empty App-text-small">No osteotomies</div>
      ) : (
        items.map((act, index) => (
          <OsteotomyItem
            key={`${act.vertebra}-${index}-${act._id || "new"}`}
            osteotomy={act}
            onEdit={() => {}}
            isSelected={false}
            onToggleSelect={() => {}}
            disabled={true}
            showCheckbox={false}
            showActions={false}
          />
        ))
      )}
    </div>
  );

  if (compareMode) {
    if (osteotomyActs.length === 0 && comparedOsteotomies.length === 0) {
      return null;
    }

    return (
      <WidgetBox
        title={"Osteotomies"}
        isSmall={true}
        isInSideBar={true}
        isExpandable={true}
      >
        <div className="side-widget-history history-compare-wrapper">
          {renderPlanSection(currentPlanLabel, osteotomyActs)}
          <div className="history-compare-separator" />
          {renderPlanSection(comparedPlanLabel, comparedOsteotomies)}
        </div>
      </WidgetBox>
    );
  }

  return (
    <WidgetBox
      title={"Osteotomies"}
      isSmall={true}
      isInSideBar={true}
      isExpandable={true}
    >
      <div className="side-widget-history">
        {/* Selection controls */}
        <div className="history-selection-controls">
          <label className="select-all-checkbox">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={isLoading}
            />
            <span className="App-text-small">
              {isAllSelected ? "Deselect All" : "Select All"}
            </span>
          </label>
          {hasSelection && (
            <span className="App-text-small selection-count">
              {selectedItems.size} selected
            </span>
          )}
        </div>

        {/* Items list */}
        {osteotomyActs.map((act, index) => (
          <OsteotomyItem
            key={`${act.vertebra}-${index}-${act._id || "new"}`}
            osteotomy={act}
            onDelete={handleDeleteOsteotomy}
            onEdit={() => {
              editOsteotomy(act);
            }}
            isSelected={selectedItems.has(generateSurgicalActKey(act))}
            onToggleSelect={handleToggleSelect}
            disabled={isLoading}
          />
        ))}

        {/* Bulk delete button */}
        {hasSelection && (
          <div className="bulk-delete-container">
            <Button
              value={`Delete (${selectedItems.size})`}
              onClick={handleBulkDelete}
              disabled={isLoading}
              className="bulk-delete-button"
            />
          </div>
        )}
      </div>
    </WidgetBox>
  );
};

export default OsteotomyHistory;
